
import { TriageData, TriageOutcome, ClinicalRule } from '@/types/triage';

// Clinical rules based on Manchester Triage System and common medical guidelines
const clinicalRules: ClinicalRule[] = [
  // IMMEDIATE PRIORITY (Red) - Life-threatening
  {
    id: 'chest-pain-severe',
    name: 'Severe Chest Pain',
    description: 'Chest pain with high-risk features',
    triggers: {
      symptoms: ['chest pain'],
      demographics: { age: { min: 40 } }
    },
    outcome: {
      urgency: 'immediate',
      redFlag: 'Chest pain in adult - possible cardiac event',
      recommendation: 'Seek immediate emergency medical attention. Call emergency services.'
    }
  },
  {
    id: 'breathing-difficulty-severe',
    name: 'Severe Breathing Difficulty',
    description: 'Shortness of breath with severity indicators',
    triggers: {
      symptoms: ['shortness of breath'],
      vitals: { heartRate: { min: 100 } }
    },
    outcome: {
      urgency: 'immediate',
      redFlag: 'Severe respiratory distress',
      recommendation: 'Immediate medical evaluation required. Go to emergency department.'
    }
  },
  {
    id: 'high-fever-elderly',
    name: 'High Fever in Elderly',
    description: 'Fever >101.5°F in patients >65',
    triggers: {
      vitals: { temperature: { min: 101.5 } },
      demographics: { age: { min: 65 } }
    },
    outcome: {
      urgency: 'immediate',
      redFlag: 'High fever in elderly patient',
      recommendation: 'Urgent medical evaluation needed within 1 hour.'
    }
  },
  {
    id: 'pregnancy-complications',
    name: 'Pregnancy Complications',
    description: 'Concerning symptoms during pregnancy',
    triggers: {
      symptoms: ['abdominal pain', 'bleeding', 'severe headache'],
      demographics: { pregnancy: true }
    },
    outcome: {
      urgency: 'immediate',
      redFlag: 'Pregnancy complications detected',
      recommendation: 'Immediate obstetric evaluation required.'
    }
  },

  // URGENT PRIORITY (Orange) - Potentially serious
  {
    id: 'chest-pain-moderate',
    name: 'Moderate Chest Pain',
    description: 'Chest pain requiring prompt evaluation',
    triggers: {
      symptoms: ['chest pain']
    },
    outcome: {
      urgency: 'urgent',
      redFlag: 'Chest pain requires cardiac evaluation',
      recommendation: 'Seek medical attention within 2-4 hours.'
    }
  },
  {
    id: 'high-fever-adult',
    name: 'High Fever',
    description: 'Fever >100.4°F requiring attention',
    triggers: {
      vitals: { temperature: { min: 100.4 } }
    },
    outcome: {
      urgency: 'urgent',
      redFlag: 'Significant fever present',
      recommendation: 'Medical evaluation recommended within 4-6 hours.'
    }
  },
  {
    id: 'severe-pain',
    name: 'Severe Pain',
    description: 'Unbearable pain requiring prompt management',
    triggers: {
      symptoms: ['abdominal pain', 'headache'],
      // severity would be checked separately
    },
    outcome: {
      urgency: 'urgent',
      redFlag: 'Severe pain requires evaluation',
      recommendation: 'Seek medical attention within 2-4 hours for pain management.'
    }
  },

  // STANDARD PRIORITY (Yellow) - Urgent but stable
  {
    id: 'moderate-symptoms',
    name: 'Moderate Symptoms',
    description: 'Symptoms requiring routine urgent care',
    triggers: {
      symptoms: ['nausea', 'vomiting', 'diarrhea', 'cough', 'sore throat']
    },
    outcome: {
      urgency: 'standard',
      redFlag: '',
      recommendation: 'Schedule appointment with healthcare provider within 24-48 hours.'
    }
  },

  // NON-URGENT PRIORITY (Green) - Minor issues
  {
    id: 'minor-symptoms',
    name: 'Minor Symptoms',
    description: 'Minor symptoms suitable for routine care',
    triggers: {
      symptoms: ['runny nose', 'muscle aches', 'fatigue']
    },
    outcome: {
      urgency: 'non-urgent',
      redFlag: '',
      recommendation: 'Consider self-care measures or routine appointment within 1-2 weeks.'
    }
  }
];

class TriageRulesEngine {
  assess(triageData: TriageData): TriageOutcome {
    const matchedRules: ClinicalRule[] = [];
    let highestUrgency: 'immediate' | 'urgent' | 'standard' | 'non-urgent' = 'non-urgent';
    const redFlags: string[] = [];
    let recommendation = 'Continue monitoring symptoms and consider routine medical consultation.';
    
    // Check each rule against the triage data
    for (const rule of clinicalRules) {
      if (this.ruleMatches(rule, triageData)) {
        matchedRules.push(rule);
        
        // Update urgency to highest level found
        if (this.getUrgencyPriority(rule.outcome.urgency) > this.getUrgencyPriority(highestUrgency)) {
          highestUrgency = rule.outcome.urgency;
          recommendation = rule.outcome.recommendation;
        }
        
        // Collect red flags
        if (rule.outcome.redFlag) {
          redFlags.push(rule.outcome.redFlag);
        }
      }
    }
    
    // Apply additional logic based on severity and other factors
    const adjustedOutcome = this.applyAdditionalLogic(triageData, {
      urgency: highestUrgency,
      recommendation,
      redFlags
    });
    
    return {
      urgency: adjustedOutcome.urgency,
      manchesterCategory: this.getManchesterCategory(adjustedOutcome.urgency),
      presentingComplaint: triageData.chiefComplaint || 'General',
      discriminator: 'General Assessment',
      recommendation: adjustedOutcome.recommendation,
      redFlags: adjustedOutcome.redFlags,
      clinicalNotes: this.generateClinicalNotes(triageData, matchedRules),
      nextSteps: this.generateNextSteps(adjustedOutcome.urgency),
      estimatedWaitTime: this.getEstimatedWaitTime(adjustedOutcome.urgency),
      aiConfidence: 0.8 // Base confidence from rules
    };
  }

  private ruleMatches(rule: ClinicalRule, triageData: TriageData): boolean {
    const { triggers } = rule;
    
    // Check symptom triggers
    if (triggers.symptoms) {
      const hasMatchingSymptom = triggers.symptoms.some(symptom =>
        triageData.symptoms.some(patientSymptom =>
          patientSymptom.toLowerCase().includes(symptom.toLowerCase())
        )
      );
      if (!hasMatchingSymptom) return false;
    }
    
    // Check vital signs triggers
    if (triggers.vitals) {
      if (triggers.vitals.temperature) {
        const temp = parseFloat(String(triageData.vitals.temperature || '0'));
        if (triggers.vitals.temperature.min && temp < triggers.vitals.temperature.min) return false;
        if (triggers.vitals.temperature.max && temp > triggers.vitals.temperature.max) return false;
      }
      
      if (triggers.vitals.heartRate) {
        const hr = parseFloat(triageData.vitals.heartRate || '0');
        if (triggers.vitals.heartRate.min && hr < triggers.vitals.heartRate.min) return false;
        if (triggers.vitals.heartRate.max && hr > triggers.vitals.heartRate.max) return false;
      }
    }
    
    // Check demographic triggers
    if (triggers.demographics) {
      if (triggers.demographics.age) {
        const age = triageData.age || 0;
        if (triggers.demographics.age.min && age < triggers.demographics.age.min) return false;
        if (triggers.demographics.age.max && age > triggers.demographics.age.max) return false;
      }
      
      if (triggers.demographics.pregnancy !== undefined) {
        if (triggers.demographics.pregnancy !== triageData.isPregnant) return false;
      }
    }
    
    return true;
  }

  private applyAdditionalLogic(triageData: TriageData, baseOutcome: any) {
    let { urgency, recommendation, redFlags } = baseOutcome;
    
    // Escalate based on severity
    if (triageData.severity === 'unbearable' && urgency === 'standard') {
      urgency = 'urgent';
      recommendation = 'Severe symptoms require prompt medical evaluation within 2-4 hours.';
    }
    
    // Consider multiple symptoms
    if (triageData.symptoms.length >= 4 && urgency === 'non-urgent') {
      urgency = 'standard';
      recommendation = 'Multiple symptoms suggest need for medical evaluation within 24-48 hours.';
    }
    
    // Consider age factors
    if (triageData.age && triageData.age >= 75) {
      if (urgency === 'non-urgent') urgency = 'standard';
      redFlags.push('Elderly patient - increased monitoring recommended');
    }
    
    // Consider pregnancy
    if (triageData.isPregnant && urgency === 'non-urgent') {
      urgency = 'standard';
      recommendation = 'Pregnancy requires closer medical monitoring. Contact healthcare provider within 24 hours.';
    }
    
    return { urgency, recommendation, redFlags };
  }

  private getUrgencyPriority(urgency: string): number {
    switch (urgency) {
      case 'immediate': return 4;
      case 'urgent': return 3;
      case 'standard': return 2;
      case 'non-urgent': return 1;
      default: return 0;
    }
  }

  private getManchesterCategory(urgency: string): string {
    switch (urgency) {
      case 'immediate': return 'Red - Immediate (0 minutes)';
      case 'urgent': return 'Orange - Very Urgent (10 minutes)';
      case 'standard': return 'Yellow - Urgent (60 minutes)';
      case 'non-urgent': return 'Green - Standard (120 minutes)';
      default: return 'Blue - Non-urgent (240 minutes)';
    }
  }

  private generateClinicalNotes(triageData: TriageData, matchedRules: ClinicalRule[]): string {
    const notes: string[] = [];
    
    notes.push(`Patient presents with ${triageData.symptoms.length} reported symptoms: ${triageData.symptoms.join(', ')}.`);
    notes.push(`Symptom severity reported as ${triageData.severity} with ${triageData.onset} onset, lasting ${triageData.duration}.`);
    
    if (matchedRules.length > 0) {
      notes.push(`Clinical rules triggered: ${matchedRules.map(r => r.name).join(', ')}.`);
    }
    
    if (triageData.medicalHistory.length > 0) {
      notes.push(`Relevant medical history: ${triageData.medicalHistory.join(', ')}.`);
    }
    
    if (triageData.medications.length > 0) {
      notes.push(`Current medications: ${triageData.medications.join(', ')}.`);
    }
    
    return notes.join(' ');
  }

  private generateNextSteps(urgency: string): string[] {
    const baseSteps = ['Monitor symptoms', 'Stay hydrated', 'Rest as needed'];
    
    switch (urgency) {
      case 'immediate':
        return ['Seek immediate medical attention', 'Call emergency services if worsening', 'Do not drive yourself'];
      case 'urgent':
        return ['Schedule urgent medical appointment', 'Monitor for worsening symptoms', 'Return if symptoms deteriorate'];
      case 'standard':
        return [...baseSteps, 'Schedule routine medical appointment', 'Contact provider if symptoms worsen'];
      case 'non-urgent':
        return [...baseSteps, 'Consider over-the-counter remedies', 'Schedule routine follow-up if needed'];
      default:
        return baseSteps;
    }
  }

  private getEstimatedWaitTime(urgency: string): string {
    switch (urgency) {
      case 'immediate': return 'Immediate attention';
      case 'urgent': return '10-30 minutes';
      case 'standard': return '1-2 hours';
      case 'non-urgent': return '2-4 hours';
      default: return 'Variable';
    }
  }
}

export const triageRulesEngine = new TriageRulesEngine();
